import { FC, ReactElement, useState, useMemo, useEffect } from "react";
import {
  Box,
  Card,
  Typography,
  Stack,
  IconButton,
  CircularProgress,
  Tooltip,
  Fade,
} from "@mui/material";
import { KeyboardArrowLeft } from "@mui/icons-material";

import DutiesStackedBar from "./duties-stacked-bar";
import { formatNumberWithSuffix } from "@/utils";
import { responsiveFontSizes, spacing } from "@/utils/responsive";
import { chartColors } from "@/constants";

interface DutyCodeBreakdown {
  code: string;
  columnName: string;
  totalDuty: number;
  entryCount: number;
}

interface DutyAuthorityTotal {
  authority: string;
  totalDuty: number;
  entryCount: number;
  codes: DutyCodeBreakdown[];
}

interface DutyAnalyticsData {
  data?: {
    grandTotal: number;
    authorities: DutyAuthorityTotal[];
    usedLegacyFallback?: boolean;
  };
}

interface DutiesCardProps {
  data?: DutyAnalyticsData;
  isLoading?: boolean;
  isFetching?: boolean;
  totalDutiesPaid?: number;
}

// Authority display names mapping
const AUTHORITY_DISPLAY_NAMES: Record<string, string> = {
  MFN: "MFN Duty",
  SECTION_201: "Section 201",
  SECTION_232: "Section 232",
  SECTION_301: "Section 301",
  "IEEPA-Reciprocal": "IEEPA Reciprocal",
  "IEEPA-Fent": "IEEPA Fentanyl",
  "IEEPA-Russian-Oil": "IEEPA Russian Oil",
  "IEEPA-Brazil": "IEEPA Brazil",
  IEEPA: "IEEPA",
  MTB: "MTB",
  AD: "Anti-Dumping",
  CVD: "Countervailing",
  OTHER: "Other Duties",
  UNKNOWN: "Unclassified",
};

const DutiesCard: FC<DutiesCardProps> = ({
  data,
  isLoading = false,
  isFetching = false,
  totalDutiesPaid = 0,
}): ReactElement => {
  const [expandedAuthority, setExpandedAuthority] = useState<string | null>(null);
  const hasData = data?.data !== undefined;

  const authorities = useMemo(() => {
    return (data?.data?.authorities || []).filter((auth) => auth.totalDuty > 0);
  }, [data]);

  const grandTotal = useMemo(() => {
    return data?.data?.grandTotal || 0;
  }, [data]);

  const usedLegacyFallback = data?.data?.usedLegacyFallback ?? false;

  const expandedData = useMemo(() => {
    if (!expandedAuthority) return null;
    return authorities.find((auth) => auth.authority === expandedAuthority);
  }, [expandedAuthority, authorities]);

  // Memoize color index for expanded authority to avoid repeated findIndex calls
  const expandedColorIndex = useMemo(() => {
    if (!expandedAuthority) return 0;
    const idx = authorities.findIndex((a) => a.authority === expandedAuthority);
    return idx >= 0 ? idx % chartColors.length : 0;
  }, [expandedAuthority, authorities]);

  // Reset expanded state when data changes (client switch, date change, etc.)
  useEffect(() => {
    setExpandedAuthority(null);
  }, [data]);

  const handleToggleExpand = (authority: string) => {
    setExpandedAuthority(expandedAuthority === authority ? null : authority);
  };

  const handleClose = () => {
    setExpandedAuthority(null);
  };

  // Show full-card loading only on initial load (or when we have no data yet).
  // Allow background refetches to keep content visible and use the small inline spinner.
  if (isLoading || (!hasData && isFetching)) {
    return (
      <Card
        sx={{
          p: { xs: 1, md: 1.25 },
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress size={40} />
      </Card>
    );
  }

  const isExpanded = Boolean(expandedAuthority);

  return (
    <Card
      sx={{
        p: { xs: 1, md: 1.25 },
        height: "100%",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Expanded View - Animates over the header */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          bgcolor: "background.paper",
          zIndex: isExpanded ? 10 : -1,
          opacity: isExpanded ? 1 : 0,
          transform: isExpanded ? "translateY(0)" : "translateY(20px)",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          p: { xs: 1, md: 1.25 },
          display: "flex",
          flexDirection: "column",
        }}
      >
        {expandedData && (
          <>
            {/* Expanded Header */}
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ mb: 1.5 }}
            >
              <IconButton
                size="small"
                onClick={handleClose}
                sx={{
                  bgcolor: "rgba(0,0,0,0.05)",
                  "&:hover": { bgcolor: "rgba(0,0,0,0.1)" },
                }}
              >
                <KeyboardArrowLeft />
              </IconButton>
              <Box
                sx={{
                  width: 14,
                  height: 14,
                  borderRadius: "3px",
                  bgcolor: chartColors[expandedColorIndex],
                  flexShrink: 0,
                }}
              />
              <Typography variant="h6" fontWeight={600} noWrap sx={{ flex: 1 }}>
                {AUTHORITY_DISPLAY_NAMES[expandedData.authority] || expandedData.authority}
              </Typography>
              <Typography variant="h6" fontWeight={700} color="primary.main">
                ${formatNumberWithSuffix(expandedData.totalDuty)}
              </Typography>
            </Stack>

            {/* Code List */}
            <Box
              sx={{
                flex: 1,
                overflow: "auto",
                maxHeight: 220,
                "&::-webkit-scrollbar": { width: 4 },
                "&::-webkit-scrollbar-thumb": {
                  bgcolor: "rgba(0,0,0,0.15)",
                  borderRadius: 2,
                },
              }}
            >
              <Stack spacing={0.5}>
                {expandedData.codes.map((code, idx) => (
                    <Fade in key={code.code} style={{ transitionDelay: `${idx * 30}ms` }}>
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{
                          py: 0.5,
                          px: 0.75,
                          borderRadius: 1,
                          transition: "all 0.15s",
                          "&:hover": {
                            bgcolor: "rgba(0,0,0,0.03)",
                          },
                        }}
                      >
                        {/* Color bar indicator */}
                        <Box
                          sx={{
                            width: 3,
                            height: 28,
                            borderRadius: 1,
                            bgcolor: chartColors[expandedColorIndex],
                            flexShrink: 0,
                          }}
                        />
                        {/* Code pill */}
                        <Typography
                          fontFamily="monospace"
                          fontWeight={600}
                          sx={{
                            fontSize: 11,
                            bgcolor: "rgba(0,0,0,0.06)",
                            px: 0.75,
                            py: 0.25,
                            borderRadius: 0.75,
                            flexShrink: 0,
                          }}
                        >
                          {code.code}
                        </Typography>
                        {/* Column name - flex grow */}
                        <Tooltip title={code.columnName} arrow placement="top">
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              flex: 1,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              fontSize: 11,
                            }}
                          >
                            {code.columnName}
                          </Typography>
                        </Tooltip>
                        {/* Entry count */}
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: 10, flexShrink: 0 }}
                        >
                          {code.entryCount}
                        </Typography>
                        {/* Amount */}
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          sx={{ minWidth: 65, textAlign: "right", fontSize: 12 }}
                        >
                          ${formatNumberWithSuffix(code.totalDuty)}
                        </Typography>
                      </Stack>
                    </Fade>
                ))}
              </Stack>
            </Box>
          </>
        )}
      </Box>

      {/* Main View - Header & List */}
      <Box
        sx={{
          opacity: isExpanded ? 0 : 1,
          transform: isExpanded ? "scale(0.95)" : "scale(1)",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          pointerEvents: isExpanded ? "none" : "auto",
        }}
      >
        <Box mb={spacing.md}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
          >
            <Box>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography
                  color="neutral.500"
                  variant="body1"
                  fontWeight={500}
                >
                  Duties by Authority
                </Typography>
                {usedLegacyFallback && (
                  <Tooltip title="Using aggregated data. Re-upload trade data for detailed code breakdown.">
                    <Box
                      sx={{
                        fontSize: 10,
                        px: 0.75,
                        py: 0.25,
                        bgcolor: "warning.light",
                        color: "warning.dark",
                        borderRadius: 1,
                        fontWeight: 600,
                      }}
                    >
                      LEGACY
                    </Box>
                  </Tooltip>
                )}
              </Stack>
              <Typography
                color="neutral.1000"
                fontFamily="Plus Jakarta Sans"
                fontWeight={700}
                variant="h2"
                sx={{
                  fontSize: responsiveFontSizes["3xl"],
                  lineHeight: 1.1,
                  mt: spacing.sm,
                }}
              >
                ${formatNumberWithSuffix(grandTotal)}
              </Typography>
            </Box>
            {isFetching && <CircularProgress size={16} />}
          </Stack>
          <Box mt={spacing.md}>
            <DutiesStackedBar dutiesPaid={totalDutiesPaid} dutiesDue={grandTotal} />
            <Stack direction="row" spacing={{ xs: 1.5, md: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Box
                  sx={{
                    width: { xs: 8, md: 10 },
                    height: { xs: 8, md: 10 },
                    bgcolor: "#10B981",
                    borderRadius: "2px",
                  }}
                />
                <Typography variant="caption" fontWeight={500}>
                  Duties Paid
                </Typography>
                <Typography variant="caption" fontWeight={700}>
                  ${formatNumberWithSuffix(totalDutiesPaid)}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <Box
                  sx={{
                    width: { xs: 8, md: 10 },
                    height: { xs: 8, md: 10 },
                    bgcolor: "#E5E7EB",
                    borderRadius: "2px",
                  }}
                />
                <Typography variant="caption" fontWeight={500}>
                  Duties Due
                </Typography>
                <Typography variant="caption" fontWeight={700}>
                  ${formatNumberWithSuffix(grandTotal)}
                </Typography>
              </Stack>
            </Stack>
          </Box>
        </Box>

        {/* Authority List - Prefer ~4 rows on taller viewports, clamp to ~3 rows on shorter ones */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflowY: "auto",
            overflowX: "hidden",
            // ~215px shows ~4 items; ~160px shows ~3 items. `vh` makes this responsive across resolutions.
            maxHeight: "clamp(160px, 22vh, 215px)",
            scrollbarGutter: "stable",
            "&::-webkit-scrollbar": { width: 4 },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: "rgba(0,0,0,0.15)",
              borderRadius: 2,
            },
          }}
        >
          <Stack spacing={0.5}>
            {authorities.map((auth, index) => {
              const displayName =
                AUTHORITY_DISPLAY_NAMES[auth.authority] || auth.authority;
              const color = chartColors[index % chartColors.length];
              const percentage =
                grandTotal > 0 ? (auth.totalDuty / grandTotal) * 100 : 0;

              return (
                <Box
                  key={auth.authority}
                  onClick={() => auth.codes.length > 0 && handleToggleExpand(auth.authority)}
                  sx={{
                    cursor: auth.codes.length > 0 ? "pointer" : "default",
                    padding: "8px 10px",
                    borderRadius: "8px",
                    backgroundColor: "transparent",
                    border: "1px solid transparent",
                    transition: "all 0.2s ease",
                    ...(auth.codes.length > 0 && {
                      "&:hover": {
                        backgroundColor: "rgba(0, 0, 0, 0.03)",
                        borderColor: "rgba(0, 0, 0, 0.08)",
                        transform: "translateX(2px)",
                      },
                    }),
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Stack direction="row" alignItems="center" spacing={1} flex={1} minWidth={0}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          background: color,
                          borderRadius: "3px",
                          flexShrink: 0,
                        }}
                      />
                      <Typography variant="body2" fontWeight={500} noWrap sx={{ minWidth: 0 }}>
                        {displayName}
                      </Typography>
                    </Stack>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ minWidth: 40, textAlign: "right" }}
                      >
                        {percentage.toFixed(1)}%
                      </Typography>
                      <Typography variant="body2" fontWeight={700} sx={{ minWidth: 70, textAlign: "right" }}>
                        ${formatNumberWithSuffix(auth.totalDuty)}
                      </Typography>
                    </Stack>
                  </Stack>
                  {/* Mini progress bar */}
                  <Box
                    sx={{
                      mt: 0.75,
                      height: 3,
                      bgcolor: "rgba(0,0,0,0.06)",
                      borderRadius: 1.5,
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        height: "100%",
                        width: `${percentage}%`,
                        bgcolor: color,
                        borderRadius: 1.5,
                        transition: "width 0.5s ease-out",
                      }}
                    />
                  </Box>
                </Box>
              );
            })}

            {authorities.length === 0 && !isLoading && (
              <Box
                sx={{
                  textAlign: "center",
                  py: 4,
                  color: "text.secondary",
                }}
              >
                <Typography variant="body2">
                  No duty data available for this period
                </Typography>
              </Box>
            )}
          </Stack>
        </Box>
      </Box>
    </Card>
  );
};

export default DutiesCard;
